import React, { useState, useEffect, useCallback } from 'react';
import FocusedCard from '../FocusedCard/Container';
import { toTitleCase } from '../../utilities/String';

const Body = props => {
    const [focusedCard, setFocusedCard] = useState(null);
    const [search, setSearch] = useState('');
    const [suitFilter, setSuitFilter] = useState('all');
    const [showAllCards, setShowAllCards] = useState(false);

    const drawACard = useCallback(() => {
        const card = props.cards[Math.floor(Math.random() * props.cards.length)];
        setFocusedCard(card);
    }, [props.cards]);

    useEffect(() => { drawACard() }, [drawACard]);

    const renderList = (data) => data.map((text, i) => (<CardListItem key={i} text={text} />));
    
    const renderBadges = (data) => data.map((text, i) => (<CardBadge key={i} text={text} />));

    const searchFilter = (card) => {
        const suitMatches = suitFilter === 'all' || card.suit.toLowerCase() === suitFilter;

        if (!suitMatches) {
            return false;
        }

        if (search === '') {
            return true;
        }

        const searchTerm = search.toLowerCase();

        return  card.name.toLowerCase().indexOf(searchTerm) !== -1 
        ||      card.suit.toLowerCase().indexOf(searchTerm) !== -1;
    }

    const filteredCards = props.cards.filter(searchFilter);
    const visibleCards = showAllCards ? filteredCards : filteredCards.slice(0, 24);
    const suitOptions = ['all', ...new Set(props.cards.map((card) => card.suit.toLowerCase()))];

    const renderCardButtons = (cards) => {
        return cards.map((card, i) => (
            <CardButton
                key={i}
                card={card}
                setFocusedCard={() => setFocusedCard(card)}
            />
        ));
    }

    return (
    <section className="grid gap-6 pb-6 lg:min-h-[calc(100vh-3rem)] lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-stretch lg:pb-0">
        <div className="min-w-0 space-y-6 lg:h-full">
            {focusedCard !== null && (
                <>
                    <div className="h-full rounded-3xl border border-slate-200 bg-white text-slate-900 shadow-xl lg:min-h-[calc(100vh-3rem)]">
                        <div className="grid h-full gap-8 p-5 sm:p-7 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                            <FocusedCard
                                card={focusedCard}
                                renderList={renderList}
                                renderBadges={renderBadges}
                            />

                            <div className="space-y-4">
                                <div className="rounded-2xl border border-slate-200 bg-slate-50">
                                    <div className="space-y-3 p-4 sm:p-5">
                                        <h6 className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
                                            Light meanings
                                        </h6>
                                        <div className="space-y-2 text-slate-700">
                                            {renderList(focusedCard.meanings.light)}
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-slate-200 bg-slate-50">
                                    <div className="space-y-3 p-4 sm:p-5">
                                        <h6 className="text-sm font-semibold uppercase tracking-[0.2em] text-rose-700">
                                            Shadow meanings
                                        </h6>
                                        <div className="space-y-2 text-slate-700">
                                            {renderList(focusedCard.meanings.shadow)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>

        <aside className="overflow-hidden rounded-3xl border border-slate-200 bg-white text-slate-900 shadow-xl lg:sticky lg:top-6 lg:h-[calc(100vh-3rem)] lg:self-start">
            <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-5 sm:p-6">
                <button onClick={drawACard} className="inline-flex w-full items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-slate-700">
                    Draw A Card
                </button>

                <label className="form-control">
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Search by card or suit"
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </label>

                <div className="flex flex-wrap gap-2">
                    {suitOptions.map((suit) => (
                        <button
                            key={suit}
                            onClick={() => {
                                setSuitFilter(suit);
                                setShowAllCards(false);
                            }}
                            className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide transition ${suitFilter === suit ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:text-slate-900'}`}>
                            {toTitleCase(suit)}
                        </button>
                    ))}
                </div>

                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="grid w-full grid-cols-1 gap-2">
                        {renderCardButtons(visibleCards)}
                    </div>
                </div>

                {filteredCards.length > 24 && (
                    <button
                        onClick={() => setShowAllCards((current) => !current)}
                        className="inline-flex w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-slate-900">
                        {showAllCards ? 'Show fewer cards' : `Show all ${filteredCards.length} cards`}
                    </button>
                )}
            </div>
        </aside>
    </section>
    )
}

export default Body;

const CardButton = props => (
    <button
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-slate-300 hover:bg-slate-50"
        onClick={()=>props.setFocusedCard()}>
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900">
            {toTitleCase(props.card.name)}
        </p>
    </button>
)

const CardBadge = props => (
    <span
        className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-slate-600">
        {props.text}
    </span>
);

const CardListItem = props => (<p className="pr-1 text-[15px] leading-relaxed">{props.text}</p>);